import React, { useState } from 'react';
import { useColor } from "../../contexts/ColorContext";
import classNames from "classnames";
import { ReactComponent as InfoSvg } from "../../assets/svg/info-circle.svg";
import GraddeSmallLogo from "../../assets/logo/gradde-small.png";
import GraddeSmallWhiteLogo from "../../assets/logo/gradde-small-white.png";
import { Button } from "react-bootstrap";
import { ReactComponent as TrashSvg } from "../../assets/svg/trash.svg";
import PortaledTooltip from '../common/PortaledTooltip';
import 'react-tooltip/dist/react-tooltip.css';
import AddGradingSystem from "../modals/add/AddGradingSystem";
import { GradingSystemEntity } from "../../types";

export type GradingItemMode = 'grading_scheme' | 'rubric';

interface SelectGradingSystemItemProps {
    itemKey?: any;
    className?: string;
    style?: React.CSSProperties;
    gradingSystem: GradingSystemEntity & { is_not_school_owned?: boolean };
    selected?: boolean;
    mode?: GradingItemMode;
    onClick?: () => void;
    onAdded?: (gradingSystem: any) => void;
    onDelete?: (gradingSystem: GradingSystemEntity) => void;
    existingNames?: string[];
    templateNames?: string[];
}

const modeLabels: Record<GradingItemMode, { ownedTooltip: string; defaultTooltip: string }> = {
    grading_scheme: {
        ownedTooltip: 'Your school owns this grading scheme',
        defaultTooltip: 'This is a grading scheme owned by Gradde, you can customize it',
    },
    rubric: {
        ownedTooltip: 'Your school owns this rubric',
        defaultTooltip: 'This is a rubric owned by Gradde, you can customize it',
    },
};

const SelectGradingSystemItem: React.FC<SelectGradingSystemItemProps> = ({
    itemKey,
    className,
    style,
    gradingSystem,
    selected = false,
    mode = 'grading_scheme',
    onClick,
    onAdded,
    onDelete,
    existingNames = [],
    templateNames = []
}) => {
    const { colors } = useColor();
    const [showAddGradingSystem, setShowAddGradingSystem] = useState<boolean>(false);
    const cardStyle: React.CSSProperties = {
        ...style,
        minWidth: '150px',
        borderRadius: '8px',
        padding: `12px 16px`,
        backgroundColor: selected ? colors.alternatePrimary : colors.white
    };

    const cardClasses = classNames(
        className,
        'card-widget',
        'd-flex',
        'justify-content-between',
        'flex-column',
        !selected && 'clickable'
    );

    return (
        <>
            <div className={cardClasses} style={cardStyle} onClick={onClick}>
                <div className='d-flex align-items-center'
                    style={{ color: !selected ? colors.primary : colors.white }}>
                    <div className='font-xs font-bold'>
                        {gradingSystem.name}
                    </div>
                    <div className='d-flex flex-column'>
                        <InfoSvg
                            style={{ color: selected ? colors.white : colors.alternatePrimary }}
                            id={"grade-system-info-" + itemKey}
                            className='mx-1 hoverable' />

                        {
                            (gradingSystem as any).is_not_school_owned &&
                            <img src={selected ? GraddeSmallWhiteLogo : GraddeSmallLogo} width={'25px'} alt="logo" />
                        }

                    </div>

                </div>

                <div className="d-flex align-items-center gap-2 mt-2 align-self-center">
                    <Button
                        onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            setShowAddGradingSystem(true);
                        }}
                        className='border-0'
                        style={{
                            backgroundColor: !selected ? colors.primary : colors.white,
                            color: selected ? colors.primary : colors.white,
                            borderRadius: '17px',
                            width: '68px'
                        }}>
                        View
                    </Button>
                    {onDelete && !(gradingSystem as any).is_not_school_owned && (
                        <button
                            type="button"
                            className="border-0 bg-transparent p-0"
                            aria-label={`Delete ${gradingSystem.name}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                onDelete(gradingSystem);
                            }}
                        >
                            <TrashSvg width={16} height={16} style={{ color: '#FF3633' }} />
                        </button>
                    )}
                </div>

                <PortaledTooltip
                    anchorSelect={"#grade-system-info-" + itemKey}
                    place="top"
                    content={
                        (gradingSystem as any).is_not_school_owned ?
                            modeLabels[mode].defaultTooltip :
                            modeLabels[mode].ownedTooltip
                    }
                />
            </div>
            <AddGradingSystem show={showAddGradingSystem}
                gradingSystem={gradingSystem as any}
                mode={mode}
                existingNames={existingNames}
                templateNames={templateNames}
                onAdded={(gradingSystem: any) => onAdded && onAdded(gradingSystem)}
                onHide={() => { setShowAddGradingSystem(false); }} />
        </>
    );
};

export default SelectGradingSystemItem;
